import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../theme";

const STORAGE_KEY = "sunsure-color-mode";

const ThemeModeContext = createContext();

const PREFERENCES = ["light", "dark", "system"];

function readStoredPreference() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return PREFERENCES.includes(stored) ? stored : "light";
  } catch {
    return "light";
  }
}

function systemPrefersDark() {
  return typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches);
}

export const ThemeModeProvider = ({ children }) => {
  const [preference, setPreference] = useState(readStoredPreference);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Follow the operating system while the preference is "system".
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return undefined;
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  const mode = preference === "system" ? (systemDark ? "dark" : "light") : preference;

  const setMode = useCallback((nextMode) => {
    const next = PREFERENCES.includes(nextMode) ? nextMode : "light";
    setPreference(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort.
    }
  }, []);

  const toggleColorMode = useCallback(() => {
    setMode(mode === "dark" ? "light" : "dark");
  }, [mode, setMode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({ mode, preference, setMode, toggleColorMode }),
    [mode, preference, setMode, toggleColorMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};
